import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChildren } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { AutomationRulesService } from '@app/features/automation-rules/automation-rules.service';
import { RulesTag } from '@app/features/automation-rules/utils';
import {
  FilterEntities,
  SkusResponseType,
  TagResponseType,
} from '@app/pages/workspace/assign/components/advanced-filters/config/models';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { Subscription } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { RuleTemplateService } from '../rule-template.service';

@Component({
  selector: 'app-split-order-rule',
  templateUrl: './split-order-rule.component.html',
  styleUrls: ['./split-order-rule.component.scss', '../order-created/order-created-template.component.scss'],
  standalone: false,
})
export class SplitOrderRuleComponent implements OnInit {
  @Input() currentActionSelectValue;
  @Input() selectedActionValue;
  @Input() conditions;
  @Output() onChangeActionSelect = new EventEmitter();
  @ViewChildren('tagsInput') tagsInputs;

  currentSplitMethod: DropdownOption;
  currentSplitMethodOperator: DropdownOption;
  currentSplitMethodOperator2: DropdownOption;
  splitMethodOptions: DropdownOption[];
  splitOrderActionFreeText: string;
  tagName: string;
  isProductTagsSplitOnAutocompleteMode: boolean;
  currentProductTagsSplitOperator: DropdownOption;
  currentSelectedProductTags: RulesTag[] = [];
  protuctTagsActionAutocompleteSubscription: Subscription;
  isLoadingProductTagsResults: boolean = false;
  actionProductTagsAutocompleteControl = new UntypedFormControl();
  productTags: RulesTag[] = [];
  autocompleteSplitMethods = ['product_tag', 'sku_list'];
  anyAllOperatorsDropdown: DropdownOption[] = [
    {
      value: 'any',
      label: 'any of',
    },
    {
      value: 'all',
      label: 'all of',
    },
  ];
  tempProductTagSplitOperator2 = [
    {
      value: 'includes',
      label: 'includes',
    },
    {
      value: 'contains',
      label: 'contains',
    },
  ];
  constructor(
    private automationRulesService: AutomationRulesService,
    private changeDetectorRef: ChangeDetectorRef,
    private ruleTemplateService: RuleTemplateService,
  ) {}

  ngOnInit(): void {
    this.initActionProductTagSearchTerm();

    this.splitMethodOptions = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.conditions,
      conditionName: 'splitOrderBy',
      labelField: 'value',
      isRawValues: true,
      displayLabelFunc: (strValue) => strValue?.split('_')?.join(' '),
    });
    if (this.selectedActionValue?.value && this.selectedActionValue?.label) {
      this.currentSplitMethod = { value: this.selectedActionValue.value, label: this.selectedActionValue.label };
      this.currentSplitMethodOperator =
        this.anyAllOperatorsDropdown.find((item) => item.value === this.selectedActionValue?.operator) ?? null;
      this.currentSplitMethodOperator2 =
        this.tempProductTagSplitOperator2.find((item) => item.value === this.selectedActionValue?.operator2) ?? null;

      if (['includes', 'excludes'].includes(this.selectedActionValue?.operator2)) {
        this.currentSelectedProductTags = [...(this.selectedActionValue?.productTags ?? [])];
      } else {
        this.currentSelectedProductTags = [];
      }

      if (['contains', 'notContains'].includes(this.selectedActionValue?.operator2)) {
        this.splitOrderActionFreeText = this.selectedActionValue?.freeTextValue ?? null;
      } else {
        this.splitOrderActionFreeText = null;
      }
      this.tagName = this.selectedActionValue?.tagName ?? null;

      this.updateSplitOrderActionAutocompleteMode(this.currentSplitMethodOperator2?.value as any);
    }
  }

  onProductTagSelect(tag: RulesTag) {
    this.actionProductTagsAutocompleteControl.reset('', { onlySelf: true });
    if (!this.currentSelectedProductTags?.find((val) => val.name === tag.name)) {
      this.currentSelectedProductTags.push(tag);
      const productTagActionInput = this.tagsInputs.find((tagInput: any) => tagInput.id == 'productTagsInput');
      productTagActionInput?.input?.nativeElement?.focus();
      this.setSplitOrderProductTags(this.currentSelectedProductTags);
    }
  }

  onProductTagRemove(tagIndex: number) {
    this.currentSelectedProductTags.splice(tagIndex, 1);
    this.setSplitOrderProductTags(this.currentSelectedProductTags);
  }

  initActionProductTagSearchTerm() {
    this.productTags = [];
    let subscription;
    const pipe = this.actionProductTagsAutocompleteControl.valueChanges.pipe(
      debounceTime(500),
      tap(() => (this.isLoadingProductTagsResults = true)),
      switchMap((value) => {
        if (value?.trim().length > 0) {
          return this.automationRulesService.getAutoComplete(
            value?.trim(),
            this.currentSplitMethod?.value === 'product_tag' ? FilterEntities.TAG : FilterEntities.SKU,
          );
        } else {
          this.productTags = [];
          this.isLoadingProductTagsResults = false;
          this.changeDetectorRef.detectChanges();
          return [];
        }
      }),
    );

    subscription = pipe.subscribe((res: TagResponseType | SkusResponseType) => {
      if (this.currentSplitMethod?.value === 'product_tag') {
        this.productTags = (res as TagResponseType).productTags.map((tag) => ({
          name: tag.name,
          type: 'tableProductTag',
        }));
      } else {
        this.productTags = (res as SkusResponseType).skus.map((sku) => ({
          name: sku,
          type: 'tableOrderTag',
        }));
      }
      this.changeDetectorRef.detectChanges();
      this.isLoadingProductTagsResults = false;
    });
    this.protuctTagsActionAutocompleteSubscription = subscription;
  }

  setSplitOrderProductTags(productTags: RulesTag[]) {
    const selectedAction = {
      ...this.currentActionSelectValue,
    };
    selectedAction.value = {
      ...this.currentSplitMethod,
      operator: this.currentSplitMethodOperator?.value,
      operator2: this.currentSplitMethodOperator2?.value,
      productTags: [...productTags],
      freeTextValue: this.splitOrderActionFreeText,
      tagName: this.currentSplitMethod.value === 'sku_list' ? this.tagName : null,
    };
    this.onChangeActionSelect.emit(selectedAction);
  }

  setSplitOrderMethod(data: DropdownOption) {
    const selectedAction = {
      ...this.currentActionSelectValue,
    };
    selectedAction.value = data;
    this.onChangeActionSelect.emit(selectedAction);

    if (this.currentSplitMethod?.value !== data?.value) {
      this.currentSplitMethod = data;
      this.currentSplitMethodOperator = null;
      this.currentSplitMethodOperator2 = null;
      this.isProductTagsSplitOnAutocompleteMode = false;
      this.splitOrderActionFreeText = null;
      this.tagName = null;
      this.currentSelectedProductTags = [];
      this.productTags = [];
      this.actionProductTagsAutocompleteControl.reset('', { onlySelf: true });
    }
  }

  onSplitProductTagFreeTextChanged(value) {
    const selectedAction = {
      ...this.currentActionSelectValue,
    };
    selectedAction.value = {
      ...this.currentSplitMethod,
      operator: this.currentSplitMethodOperator?.value,
      operator2: this.currentSplitMethodOperator2?.value,
      productTags: [...this.currentSelectedProductTags],
      freeTextValue: value,
      tagName: this.currentSplitMethod.value === 'sku_list' ? this.tagName : null,
    };

    this.splitOrderActionFreeText = value;
    this.onChangeActionSelect.emit(selectedAction);
  }

  onTagNameTextChanged(value) {
    const selectedAction = {
      ...this.currentActionSelectValue,
    };
    selectedAction.value = {
      ...this.currentSplitMethod,
      operator: this.currentSplitMethodOperator?.value,
      operator2: this.currentSplitMethodOperator2?.value,
      productTags: [...this.currentSelectedProductTags],
      freeTextValue: this.splitOrderActionFreeText,
      tagName: value,
    };

    this.tagName = value;
    this.onChangeActionSelect.emit(selectedAction);
  }

  setSplitOrderMethodOperator2(data: DropdownOption) {
    const selectedAction = {
      ...this.currentActionSelectValue,
    };
    selectedAction.value = {
      ...this.currentSplitMethod,
      operator: this.currentSplitMethodOperator?.value,
      operator2: data?.value,
      productTags: [...this.currentSelectedProductTags],
      freeTextValue: this.splitOrderActionFreeText,
      tagName: this.currentSplitMethod.value === 'sku_list' ? this.tagName : null,
    };

    this.currentSplitMethodOperator2 = data;
    this.updateSplitOrderActionAutocompleteMode(data?.value as any);
    this.onChangeActionSelect.emit(selectedAction);
  }

  updateSplitOrderActionAutocompleteMode(operator: string | null) {
    this.isProductTagsSplitOnAutocompleteMode = ['includes', 'excludes'].includes(operator);
  }

  setSplitOrderMethodOperator(data: DropdownOption) {
    const selectedAction = {
      ...this.currentActionSelectValue,
    };
    selectedAction.value = {
      ...this.currentSplitMethod,
      operator: data?.value,
      operator2: this.currentSplitMethodOperator2?.value,
      productTags: [...this.currentSelectedProductTags],
      freeTextValue: this.splitOrderActionFreeText,
      tagName: this.currentSplitMethod.value === 'sku_list' ? this.tagName : null,
    };

    this.currentSplitMethodOperator = data;
    this.onChangeActionSelect.emit(selectedAction);
  }
}
